import { Heading, StickyFooter } from '@/components';
import {
  CustomerDetailsCard,
  PackageAndPaymentCard,
  ProductDetailsCard,
  OrderIdCard,
  DeliveryInstructionCard,
  PickupAddressCard,
} from '@/components/OrderForm';
import NoCarrierModal from '@/components/OrderForm/NoCarrierModal';
import { resolveProvinceName } from '@/utils/provinceLookup';
import { ROUTE } from '@/config';
import { useChangeProduct, useUpdateOrderPricePreview } from '@/hooks';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { useOrderByUID, useUpdateOrder } from '@/hooksApi/useOrder';
import { useFetchPickupAddresses, useGetAvailableCarriers } from '@/hooksApi';
import { useEvaluateShippingRules } from '@/hooksApi/useShippingRule';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import useNewOrderStore from '@/stores/useNewOrderStore';
import useProductStore from '@/stores/productStore';
import {
  AvailableCarrierItem,
  AvailableCarriersAdaptedResponse,
  BasicProduct,
  Order,
  OrderItem,
  OrderOptionEnum,
  ServiceTypeEnum,
} from '@/types';
import {
  adjustPriceByOrderType,
  adjustProductListByOrderType,
  checkProductValidation,
  getOrderOptions,
  getOrderPickupAddress,
  getOrderTypeTextKey,
  parsePhoneNumber,
  buildAvailableCarriersRequest,
  processCarrierPreviewResponse,
} from '@/utils';
import { buildEvaluateShippingRulesRequest } from '@/utils/buildEvaluateShippingRulesRequest';
import { decideOrderRouting } from '@/utils/decideOrderRouting';
import { applyRuleDecisionToCarriers } from '@/utils/applyRuleDecisionToCarriers';
import { hasCarrierRelevantOrderChange } from '@/utils/hasCarrierRelevantOrderChange';
import { Flex, Form, message, Spin } from 'antd';
import { Store } from 'antd/es/form/interface';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useEffect, useState, KeyboardEvent, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import { ContactData } from '@/components/OrderForm/CustomerDetailsCard';

interface EditOrderProps {
  keyValue?: string;
}

const EditOrder = ({ keyValue }: EditOrderProps) => {
  const [selectedProvinceCode, setSelectedProvinceCode] = useState<string | undefined>(undefined);
  const [showNoCarrierModal, setShowNoCarrierModal] = useState(false);
  const [currentProcessId, setCurrentProcessId] = useState<string | undefined>(undefined);
  const [recommendedCarrierSlug, setRecommendedCarrierSlug] = useState<string | undefined>(
    undefined,
  );
  const { orderType, setOrderType, resetOrderType } = useOrderTypeStore();

  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const { orderUid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Gates the auto-derive-fragile effect. Kept false while the fetched order
  // hydrates so the effect cannot clobber the restored order-level `isFragile`
  // (an order with is_fragile: true but NO individually-fragile product) before
  // the products have settled. Re-enabled once hydration finishes. Mirrors the
  // create page's fragileSyncEnabledRef.
  const fragileSyncEnabledRef = useRef(false);

  const { products, resetProducts, setSubmitAttempted } = useProductStore();
  const {
    setOrderPayload: setStoreOrderPayload,
    setCarrierSelection,
    setSelectedCarrier,
    setSourceRegionUid,
    setContactDisplayInfo,
    setRuleDispatch,
    setEditMode,
    reset: resetOrderStore,
  } = useNewOrderStore();

  const { data: pickupAddressesData } = useFetchPickupAddresses(1);

  const { mutate: fetchCarrierPreview } = useGetAvailableCarriers();
  const { mutate: evaluateRulesMutate } = useEvaluateShippingRules();

  const {
    handleFormUpdated,
    orderPrice,
    setOrderPrice,
    orderPriceLoading,
    customShipmentFee,
    setCustomShipmentFee,
    setPackageSize,
    transformValuesToOrder,
    checkOrderFormValidation,
    handleProductChanged,
  } = useUpdateOrderPricePreview(products, form, false);

  const {
    handleAddProduct,
    handleAddProducts,
    handleDeleteProduct,
    setProducts,
    handleUpdateProduct,
    handleUpdateProductBatch,
    setIsProductListValid,
  } = useChangeProduct(handleProductChanged, orderType);

  const { mutate: mutateEditOrder, isLoading: editOrderLoading } = useUpdateOrder(orderUid!);
  const {
    data: orderData,
    isLoading: displayOrderLoading,
    isFetching: displayOrderFetching,
  } = useOrderByUID(orderUid!); // Use useOrderByUID to fetch the order data
  const { data: provinceList, isLoading: provinceListLoading } = useFetchListProvinces();
  const { data: areaList, isLoading: areaListLoading } = useFetchAreaByProvinceCode(
    selectedProvinceCode!,
  );

  // Memoize the adjusted products to prevent unnecessary recalculations
  const adjustedProducts = useMemo(() => {
    if (!orderData?.object?.products) return [];
    return adjustProductPrices(orderData.object.products);
  }, [orderData?.object?.products, orderData?.object?.type]);

  useEffect(() => {
    // Reset all stores on mount to clear stale data from previous order flows
    resetOrderStore();
    form.resetFields();
    resetProducts();
    setIsProductListValid(true);
    resetOrderType();

    return () => {
      form.resetFields();
      resetProducts();
      setIsProductListValid(true);
      resetOrderType();
    };
  }, []);

  useEffect(() => {
    if (orderData && provinceList && !selectedProvinceCode) {
      fetchSelectedProvince();
    }
    if (!areaListLoading && areaList && areaList?.object.length > 0) {
      const area = areaList.object.find((item) => item.uid === orderData?.object.region?.uid);

      form.setFieldsValue({ region_uid: area?.uid });
    }
  }, [areaListLoading, areaList, orderData, keyValue, provinceList]);

  useEffect(() => {
    form.resetFields();
    // Suppress the auto-derive-fragile effect until this order finishes
    // hydrating, so it can't wipe the restored isFragile mid-hydration.
    fragileSyncEnabledRef.current = false;

    if (orderData) {
      setOrderPrice({
        ...orderData.object.preview,
      });

      setCustomShipmentFee(orderData.object.fee_customer_payable, true);

      if (orderData?.object?.products) {
        setProducts(
          adjustProductListByOrderType(
            orderData.object.products,
            orderData.object.type as OrderTypeEnum,
          ),
        );
      }

      fetchSelectedProvince();

      const primaryPhoneInfo = parsePhoneNumber(orderData.object.phone);
      const additionalPhones: string[] | undefined = orderData.object.additional_phones
        ? JSON.parse(orderData.object.additional_phones)
        : undefined;

      const secondaryPhoneInfo = parsePhoneNumber(additionalPhones?.[0]);

      // Prepopulate the form with fetched order data
      form.setFieldsValue({
        orderType: orderData.object.type,
        name: orderData.object?.full_name || '',
        address: orderData.object?.address_text || '',
        deliveryInstruction: orderData?.object?.description || '',
        packageSize: orderData?.object?.size || '',
        isFragile: orderData?.object?.is_fragile,
        temperatureControl: !!orderData?.object?.temperature_control,
        CodOrPrepaid: orderData?.object?.payment_type,
        CustomerOrMerchant: orderData?.object?.shipment_fee_type,
        orderId: orderData?.object?.custom_id,
        pickupOrDropoff: orderData?.object?.pick_up_type,
        saved_pick_up_address_uid: getOrderPickupAddress(orderData.object as unknown as OrderItem)
          ?.uid,
        freezed_pick_up_address_title:
          orderData.object.status.slug !== 'new'
            ? orderData.object.pick_up?.pick_up_address?.title
            : undefined,
        phoneNumberPrimary: primaryPhoneInfo.phone || '',
        countryCodePrimary: primaryPhoneInfo.countryCode,
        countryCodeSecondary: secondaryPhoneInfo?.countryCode,
        secondaryPhoneNumber: secondaryPhoneInfo?.phone,
        products: adjustedProducts,
        ...getOrderOptions(orderData.object.options),
      });

      // Set OrderType state after form initialization
      if (orderData.object.type) {
        setOrderType(orderData.object.type as OrderTypeEnum);
      }

      // Re-enable the fragile auto-derive only after the restored products and
      // form values have hydrated, so it can't wipe the restored isFragile.
      setTimeout(() => {
        fragileSyncEnabledRef.current = true;
      }, 0);
    }
  }, [orderData, form, keyValue, adjustedProducts, setOrderType]);

  const resolveSourceRegionUid = useCallback(
    (formValues: Store): string | undefined => {
      const selectedPickupAddress = pickupAddressesData?.object?.items?.find(
        (addr) =>
          addr.uid === formValues.saved_pick_up_address_uid ||
          addr.uid === formValues.pick_up_address_uid,
      );
      // Fallback chain for scheduled/in-progress orders where pickup address
      // may not be in the paginated list or region may not be eager-loaded
      return (
        selectedPickupAddress?.region?.uid ??
        orderData?.object?.pick_up?.pick_up_address?.region?.uid ??
        orderData?.object?.saved_pick_up_address?.region?.uid
      );
    },
    [pickupAddressesData, orderData],
  );

  // Item 4 — manual fallback decision used when rule evaluation is skipped
  // (cosmetic-only edit / non-editable order) or fails. Mirrors the create
  // flow's manual branch so SelectCarrier renders the full carrier list.
  const MANUAL_DECISION = {
    action: 'manual' as const,
    assignedCarrierUid: null,
    winningRuleUid: null,
    restrictedCarrierUids: [] as [],
    recommendedFilter: null,
  };

  // Item 4 — apply the shipping-rule flow when an order is EDITED.
  //
  // Mirrors AddNewOrder.checkCarriersAndNavigate: before fetching carriers,
  // ask the rule engine what to do, then route accordingly. Rule evaluation
  // is skipped (manual flow) when:
  //   - the order status is not editable (new/scheduled/out-for-collecting)
  //   - no carrier-relevant field actually changed (cosmetic-only edit) so
  //     the merchant is not bounced through Step 2 for a description tweak.
  // Rule evaluation failures also fall through to the manual flow so the
  // shipping-rules feature never blocks an order edit.
  const checkCarriersAndNavigate = (orderPayload: Order, formValues: Store) => {
    const source_region_uid = resolveSourceRegionUid(formValues);

    if (!source_region_uid || !orderPayload.contact?.region_uid) {
      console.warn('Cannot check carrier availability: missing pickup or destination region.');
      setShowNoCarrierModal(true);
      return;
    }

    // Only editable statuses run the rule flow; the backend blocks edits to
    // any other status anyway. Build the original order shape from the
    // fetched order so cosmetic-only edits can be detected.
    const status = orderData?.object?.status?.slug;
    const isEditableStatus =
      status === 'new' || status === 'scheduled' || status === 'out-for-collecting';

    const originalOrder: Order | null = orderData?.object
      ? ({
          size: orderData.object.size,
          is_fragile: !!orderData.object.is_fragile,
          temperature_control: !!orderData.object.temperature_control,
          type: orderData.object.type,
          payment_type: orderData.object.payment_type,
          saved_pick_up_address_uid: getOrderPickupAddress(orderData.object as unknown as OrderItem)
            ?.uid,
          contact: { region_uid: orderData.object.region?.uid } as Order['contact'],
          products: orderData.object.products,
        } as unknown as Order)
      : null;

    const shouldEvaluate =
      isEditableStatus && hasCarrierRelevantOrderChange(originalOrder, orderPayload);

    if (!shouldEvaluate) {
      proceedWithCarrierFetch(orderPayload, source_region_uid, MANUAL_DECISION);
      return;
    }

    const sourcePickup = pickupAddressesData?.object?.items?.find(
      (addr) =>
        addr.uid === formValues.saved_pick_up_address_uid ||
        addr.uid === formValues.pick_up_address_uid,
    );
    const sourceProvinceCode =
      sourcePickup?.region?.province_code ??
      orderData?.object?.pick_up?.pick_up_address?.region?.province_code ??
      '';
    const destinationProvinceCode = selectedProvinceCode || '';

    const evalReq = buildEvaluateShippingRulesRequest(
      orderPayload,
      sourceProvinceCode,
      destinationProvinceCode,
    );

    evaluateRulesMutate(evalReq, {
      onSuccess: (evalResponse: any) => {
        proceedWithCarrierFetch(orderPayload, source_region_uid, decideOrderRouting(evalResponse));
      },
      onError: (err: any) => {
        console.warn('Rule evaluation failed; falling back to manual carrier flow', err);
        proceedWithCarrierFetch(orderPayload, source_region_uid, MANUAL_DECISION);
      },
    } as any);
  };

  // Fetch carriers, apply the rule decision and navigate to Step 2 (or skip
  // straight to summary for auto-dispatched / auto-cheapest decisions).
  const proceedWithCarrierFetch = (
    orderPayload: Order,
    source_region_uid: string,
    decision: ReturnType<typeof decideOrderRouting>,
  ) => {
    const carrierRequest = buildAvailableCarriersRequest(orderPayload, source_region_uid);

    fetchCarrierPreview(carrierRequest, {
      onSuccess: (response: AvailableCarriersAdaptedResponse) => {
        const allCarriers = processCarrierPreviewResponse(response);
        setCurrentProcessId(response.process_id ?? undefined);
        setRecommendedCarrierSlug(response.recommended_carrier_slug ?? undefined);

        // Apply the rule decision via the shared reducer (identical to the
        // create flow) — restricted/auto-dispatch/auto-cheapest filtering.
        const ruleResult = applyRuleDecisionToCarriers(allCarriers, decision);
        const ruleCarriers = ruleResult.carriers;

        if (ruleCarriers.length === 0) {
          setShowNoCarrierModal(true);
          return;
        }

        // Build AvailableCarrierItem[] for the store
        const available: AvailableCarrierItem[] = ruleCarriers.map((c) => ({
          carrier_slug: c.carrier.slug || '',
          carrier_name: c.carrier.title,
          carrier_logo_url: c.carrier.avatar?.abs_url || '',
          shipping_fee: parseFloat(c.shipping_fee) || 0,
          delivery_day:
            c.serviceType === ServiceTypeEnum.SAME_DAY ? ('same' as const) : ('next' as const),
          same_day_cutoff: c.serviceType === ServiceTypeEnum.SAME_DAY ? c.if_ship_before : null,
          drop_off_time: c.serviceType === ServiceTypeEnum.NEXT_DAY ? c.if_ship_before : null,
        }));

        // Save to store and navigate to carrier selection
        setStoreOrderPayload(orderPayload);
        setSourceRegionUid(source_region_uid);
        setEditMode(orderUid!);

        // Clear stale carrier selection; re-set when the rule auto-dispatched
        // or auto-cheapest so Step 2 can be skipped.
        setSelectedCarrier(null);
        if (ruleResult.autoSelectIndex !== null && available[ruleResult.autoSelectIndex]) {
          setSelectedCarrier(available[ruleResult.autoSelectIndex]);
        }

        // Surface the decision so SelectCarrier / OrderSummary can render
        // the auto-dispatch banner and the override-reason modal.
        setRuleDispatch(ruleResult.ruleDispatch);

        // Resolve province/area display names for the summary page
        const provinceName =
          provinceList?.object?.find((p) => p.province_code === selectedProvinceCode)?.province ??
          '';
        const areaName =
          areaList?.object?.find((a) => a.uid === orderPayload.contact?.region_uid)?.area_name ??
          '';
        setContactDisplayInfo({ provinceName, areaName });
        setCarrierSelection({
          process_id: response.process_id,
          recommended_carrier_slug: response.recommended_carrier_slug,
          available_carriers: available,
        });

        // Auto-dispatched / auto-cheapest skip Step 2 — carrier already chosen.
        if (ruleResult.autoSelectIndex !== null && available[ruleResult.autoSelectIndex]) {
          navigate(ROUTE.ORDER_SUMMARY);
        } else {
          navigate(ROUTE.SELECT_CARRIER);
        }
      },
      onError: (error: any) => {
        console.error('Failed to fetch carrier availability on update:', error);
        setCurrentProcessId(undefined);
        setRecommendedCarrierSlug(undefined);
        setShowNoCarrierModal(true);
      },
    });
  };

  const onFinish = (values: Store) => {
    setSubmitAttempted(true);

    // Check for partially filled products (has some data but missing required fields)
    const partiallyFilled = products.filter((p) => {
      const hasTitle = !!p.title;
      const hasPrice = !!p.price || p.price === 0;
      return (hasTitle || hasPrice) && !(hasTitle && hasPrice);
    });

    if (partiallyFilled.length > 0) {
      message.error(t('please_fill_all_product_fields'));
      return;
    }

    // Filter out completely empty products
    const finalProducts = products.filter((p) => checkProductValidation(p));

    const orderValue = { products: finalProducts, ...values };
    const isValid = checkOrderFormValidation(orderValue);
    setIsProductListValid(finalProducts.length > 0);

    if (isValid) {
      const orderPayload: Order = transformValuesToOrder(orderValue, undefined, true);
      checkCarriersAndNavigate(orderPayload, values);
    }
  };
  const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
    const formValues = form.getFieldsValue();

    checkOrderFormValidation({ ...formValues, products });

    console.warn('Failed: ', { ...errorInfo, products });
  };

  const fetchSelectedProvince = () => {
    const provinceCode = provinceList?.object.find(
      (item) => item.province_code === orderData?.object.region?.province_code,
    )?.province_code;

    if (provinceCode && provinceCode !== selectedProvinceCode)
      setSelectedProvinceCode(provinceCode);

    form.setFieldValue('province', provinceCode);
    return provinceCode;
  };
  const handleSetSelectedProvinceCode = (provinceCode?: string) => {
    form.setFieldsValue({ province: provinceCode });
    form.setFieldsValue({ region_uid: undefined });
    form.setFieldValue('region_uid', undefined);
    setSelectedProvinceCode(provinceCode);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleCreateProduct = () => {
    handleAddProduct({ title: '', quantity: '1', price: '' });
  };

  // Auto-derive order-level fragile from products — set true when any is fragile, clear when none are.
  // Suppressed during the initial order hydration so it can't clobber the restored isFragile
  // (an order saved with is_fragile: true but no individually-fragile product).
  useEffect(() => {
    if (!fragileSyncEnabledRef.current) return;
    const anyFragile = products.some((p) => p.is_fragile === true);
    const currentFragile = form.getFieldValue('isFragile');
    if (anyFragile && !currentFragile) {
      form.setFieldsValue({ isFragile: true });
    } else if (!anyFragile && currentFragile) {
      form.setFieldsValue({ isFragile: false });
    }
  }, [products]);

  function adjustProductPrices(products: BasicProduct[]): BasicProduct[] {
    if (!products?.length) return products;

    return products.map((product) => ({
      ...product,
      // Preserve price type to avoid scientific notation for large values
      price:
        typeof product.price === 'string'
          ? String(Math.abs(parseFloat(product.price)))
          : Math.abs(Number(product.price)),
    }));
  }

  const handleOrderTypeChange = (newOrderType: OrderTypeEnum) => {
    const allValues = form.getFieldsValue();
    const updatedProducts = adjustProductListByOrderType(products, newOrderType);
    setProducts(updatedProducts);
    handleFormUpdated(
      { orderType: newOrderType },
      { ...allValues, orderType: newOrderType, products: updatedProducts },
    );
  };

  const onFormValuesChange = (changedValues: any, allValues: Store) => {
    const changedItem = Object.keys(changedValues)[0];
    if (changedItem === 'orderType') {
      const updatedProducts = adjustProductListByOrderType(products, allValues.orderType);
      setProducts(updatedProducts);
      handleFormUpdated(changedValues, { ...allValues, products: updatedProducts });
    } else {
      handleFormUpdated(changedValues, allValues);
    }
  };

  const handleUpdatePreviewByContactSelection = (contact: ContactData) => {
    const allValues = form.getFieldsValue();

    handleFormUpdated({ address: contact.address || '' }, { ...allValues, ...contact });
  };

  // Task-6 — the generic NoCarrierModal should name the destination
  // province when the true cause is "no temperature-control carrier serves
  // this route". Display-only: does not affect eligibility/submit logic.
  const noCarrierLocale = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const temperatureUnavailableProvince =
    form.getFieldValue('temperatureControl') && selectedProvinceCode
      ? resolveProvinceName(selectedProvinceCode, provinceList?.object, noCarrierLocale)
      : undefined;

  return (
    <Flex vertical>
      <NoCarrierModal
        open={showNoCarrierModal}
        onClose={() => setShowNoCarrierModal(false)}
        recommendedCarrierSlug={recommendedCarrierSlug}
        temperatureUnavailableProvince={temperatureUnavailableProvince}
      />
      {displayOrderLoading || displayOrderFetching ? (
        <Spin size='large' className='flex justify-center items-center' />
      ) : (
        <Form
          form={form}
          name='add-new-order'
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onFormValuesChange}
          onKeyDown={handleKeyPress}
        >
          {/* 3-step wizard matching create order flow */}
          <OrderStepper currentStep={1} />

          <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 mb-3'>
            {/* Left column */}
            <div className='flex flex-col gap-6 [&>*]:!mb-0'>
              <CustomerDetailsCard
                handleUpdatePreview={handleUpdatePreviewByContactSelection}
                form={form}
                selectedProvinceCode={selectedProvinceCode}
                setSelectedProvinceCode={handleSetSelectedProvinceCode}
              />
              <DeliveryInstructionCard />
              <OrderIdCard />
              <ProductDetailsCard
                products={products}
                handleCreateProduct={handleCreateProduct}
                handleDeleteProduct={handleDeleteProduct}
                handleUpdateProduct={handleUpdateProduct}
                handleUpdateProductBatch={handleUpdateProductBatch}
                handleAddProducts={handleAddProducts}
              />
              <PickupAddressCard
                form={form}
                orderStatus={orderData?.object.status.slug}
                previewOrderPrice={(formValue) =>
                  handleFormUpdated(
                    { saved_pick_up_address_uid: formValue.saved_pick_up_address_uid },
                    formValue,
                  )
                }
              />
            </div>
            {/* Right column */}
            <div className='flex flex-col gap-6 [&>*]:!mb-0'>
              <div className='sticky top-4 flex flex-col gap-4 [&>*]:!mb-0'>
                <PackageAndPaymentCard
                  orderOptions={orderData?.object?.options ?? []}
                  orderStatus={orderData?.object.status.slug}
                  form={form}
                  onPackageSizeChange={setPackageSize}
                  orderPrice={{
                    ...orderPrice,
                    orders_shipment_fee: 0,
                    orders_custom_shipment_fee: 0,
                  }}
                  orderPriceLoading={orderPriceLoading}
                  paymentType={orderData?.object?.payment_type}
                  shipmentFeeType={orderData?.object?.shipment_fee_type}
                  packageSize={orderData?.object?.size}
                  onCustomShipmentFeeChange={setCustomShipmentFee}
                  customShipmentFee={customShipmentFee}
                  discountAmount={orderData?.object.discount_amount ?? 0}
                  prepaymentAmount={orderData?.object.prepayment_amount ?? 0}
                  onOrderTypeChange={handleOrderTypeChange}
                  hasFragileProduct={products.some((p) => p.is_fragile === true)}
                />
              </div>
            </div>
          </div>

          <StickyFooter
            cardClassName='rounded-2xl'
            actionButton={
              <button
                type='submit'
                className='flex items-center gap-1.5 h-11 px-6 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-sm font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
                disabled={editOrderLoading}
              >
                {editOrderLoading && (
                  <span className='h-4 w-4 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                )}
                {t('update')}
              </button>
            }
            additionalContent={
              orderType !== OrderTypeEnum.NORMAL ? (
                <Heading
                  title={t(getOrderTypeTextKey(orderType))}
                  fontSize='text-sm'
                  fontWeight='font-bold'
                  extraClassName='uppercase leading-5 text-customTunaPrimary'
                />
              ) : null
            }
          />
        </Form>
      )}
    </Flex>
  );
};

export default EditOrder;
